import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  Button,
  Badge,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@fyos/components';
import { ArrowLeft, Settings, Activity, X, CheckSquare, Filter, LayoutGrid, List, GanttChart } from 'lucide-react';
import FilterModal from '@/components/FilterModal';
import ActivityModal from '@/components/ActivityModal';
import SettingsModal from '@/components/SettingsModal';
import AppNav from '@/components/AppNav';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { useS3UrlRefresher } from '@/hooks/useS3UrlRefresher';
import { useWorkspaceMetadataStore } from '@/stores/workspaceMetadataStore';
import { itemApi, Item } from '@/lib/itemApi';
import { workspaceMemberApi, WorkspaceMember } from '@/lib/workspaceMemberApi';
import ItemDetailModal from '@/components/ItemDetailModal';
import BoardColumn from '@/components/board/BoardColumn';
import BoardItemCard from '@/components/board/BoardItemCard';
import CreateItemModal from '@/components/board/CreateItemModal';
import BoardSkeleton from '@/components/board/BoardSkeleton';
import CommandPalette from '@/components/CommandPalette';
import BoardListView from '@/components/board/BoardListView';
import KillReasonModal from '@/components/board/KillReasonModal';
import type { ColumnDef } from '@/components/board/BoardColumn';

const FALLBACK_COLUMNS: ColumnDef[] = [
  { id: 'now', name: 'Now', description: 'Currently being worked on', color: 'bg-red-100', autoKill: 30 },
  { id: 'next', name: 'Next', description: 'Coming up soon', color: 'bg-yellow-100', autoKill: 90 },
  { id: 'later', name: 'Later', description: 'On the horizon', color: 'bg-blue-100', autoKill: 180 },
  { id: 'shipped', name: 'Shipped', description: 'Completed and released', color: 'bg-green-100', autoKill: null },
  { id: 'killed', name: 'Killed', description: 'Not happening', color: 'bg-gray-100', autoKill: null },
];

export default function RoadmapPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspaceStore();
  useS3UrlRefresher(workspaceId);

  // Subscribe to shared metadata store for columns, categories, tags
  const storeColumns = useWorkspaceMetadataStore((state) => state.columns);
  const categories = useWorkspaceMetadataStore((state) => state.categories);
  const tags = useWorkspaceMetadataStore((state) => state.tags);
  
  // Transform store columns to ColumnDef format (only active columns)
  const columns: ColumnDef[] = storeColumns.length > 0 
    ? storeColumns
        .filter(c => c.is_active)
        .map(c => ({
          id: c.slug,
          name: c.name,
          description: c.description,
          color: c.color || 'bg-gray-100',
          hexColor: c.color || undefined,
          autoKill: c.auto_kill_days ?? null,
        }))
    : FALLBACK_COLUMNS;

  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [defaultColumn, setDefaultColumn] = useState<string>('later');
  const [showColumnPicker, setShowColumnPicker] = useState(true);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [activeItem, setActiveItem] = useState<Item | null>(null);
  const [mobileTab, setMobileTab] = useState('now');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [bulkMoving, setBulkMoving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [viewMode, setViewMode] = useState<'board' | 'list' | 'timeline'>('board');
  const [listSortField, setListSortField] = useState('created');
  const [listSortDir, setListSortDir] = useState<'asc' | 'desc'>('desc');
  const [pendingKill, setPendingKill] = useState<{ itemId: string; title: string; targetColumn: string; overItemId?: string } | null>(null);

  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 767px)').matches;
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  useEffect(() => {
    if (workspaceId) {
      fetchData();
    }
  }, [workspaceId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch items and members (page-specific data)
      const [itemsData, membersData] = await Promise.all([
        itemApi.getAll(workspaceId!),
        workspaceMemberApi.getAll(workspaceId!).catch(() => []),
      ]);
      setItems(itemsData);
      setMembers(membersData);
      
      // Fetch metadata (shared store)
      await useWorkspaceMetadataStore.getState().fetchAll(workspaceId!);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getItemsByColumn = useCallback(
    (columnId: string) => {
      let filtered = items.filter(item => item.board_column === columnId);
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        filtered = filtered.filter(item =>
          item.title.toLowerCase().includes(q) ||
          (item.description && item.description.toLowerCase().includes(q))
        );
      }
      if (filterCategory) {
        filtered = filtered.filter(item => item.category_id === filterCategory);
      }
      if (filterPriority) {
        filtered = filtered.filter(item => item.priority === filterPriority);
      }
      if (filterTag) {
        filtered = filtered.filter(item => item.tags?.some(t => t.id === filterTag));
      }
      if (filterAssignee) {
        filtered = filtered.filter(item => item.assignees?.some(a => a.id === filterAssignee));
      }
      return filtered.sort((a, b) => a.board_position - b.board_position);
    },
    [items, searchQuery, filterCategory, filterPriority, filterTag, filterAssignee]
  );

  const handleDragStart = (event: DragStartEvent) => {
    const draggedItem = items.find(i => i.id === event.active.id);
    setActiveItem(draggedItem || null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveItem(null);
    const { active, over } = event;
    if (!over) return;

    const itemId = active.id as string;
    const overId = over.id as string;
    const draggedItem = items.find(i => i.id === itemId);
    if (!draggedItem) return;

    // Determine if dropped on a column or on another item
    const isColumn = columns.some(c => c.id === overId);
    const overItem = items.find(i => i.id === overId);

    // Resolve target column
    let targetColumnId: string | null = null;
    if (isColumn) {
      targetColumnId = overId;
    } else if (overItem) {
      targetColumnId = overItem.board_column;
    }

    // Intercept moves to killed column — prompt for reason
    if (targetColumnId === 'killed' && draggedItem.board_column !== 'killed') {
      setPendingKill({ itemId, title: draggedItem.title, targetColumn: 'killed', overItemId: isColumn ? undefined : overId });
      return;
    }

    await executeDragMove(itemId, overId, draggedItem, isColumn, overItem);
  };

  const executeDragMove = async (itemId: string, overId: string, draggedItem: Item, isColumn: boolean, overItem?: Item, killReason?: string) => {
    if (isColumn) {
      const targetColumnId = overId;
      if (draggedItem.board_column === targetColumnId) return;

      setItems(prev =>
        prev.map(item =>
          item.id === itemId ? { ...item, board_column: targetColumnId } : item
        )
      );

      try {
        const updatedItem = await itemApi.move(workspaceId!, itemId, {
          board_column: targetColumnId,
          kill_reason: killReason,
        });
        setItems(prev =>
          prev.map(item => (item.id === itemId ? updatedItem : item))
        );
      } catch (err) {
        console.error('Failed to move item:', err);
        setItems(prev =>
          prev.map(item =>
            item.id === itemId ? { ...item, board_column: draggedItem.board_column } : item
          )
        );
      }
    } else if (overItem) {
      const targetColumn = overItem.board_column;
      const sameColumn = draggedItem.board_column === targetColumn;

      const columnItems = items
        .filter(i => i.board_column === targetColumn && i.id !== itemId)
        .sort((a, b) => a.board_position - b.board_position);

      const overIndex = columnItems.findIndex(i => i.id === overId);
      const insertIndex = overIndex >= 0 ? overIndex : columnItems.length;

      const reordered = [...columnItems];
      reordered.splice(insertIndex, 0, { ...draggedItem, board_column: targetColumn });

      const updates = reordered.map((item, idx) => ({
        ...item,
        board_position: idx,
      }));

      setItems(prev => {
        const otherItems = prev.filter(i => i.board_column !== targetColumn && i.id !== itemId);
        return [...otherItems, ...updates];
      });

      try {
        if (!sameColumn) {
          await itemApi.move(workspaceId!, itemId, { board_column: targetColumn, kill_reason: killReason });
        }
        await itemApi.reorder(workspaceId!, updates.map(i => ({ id: i.id, board_position: i.board_position })));
      } catch (err) {
        console.error('Failed to reorder:', err);
        fetchData();
      }
    }
  };

  const handleKillConfirm = async (reason: string) => {
    if (!pendingKill) return;
    const { itemId, targetColumn, overItemId } = pendingKill;
    const draggedItem = items.find(i => i.id === itemId);
    if (!draggedItem) { setPendingKill(null); return; }

    const isColumn = !overItemId;
    const overItem = overItemId ? items.find(i => i.id === overItemId) : undefined;
    const overId = overItemId || targetColumn;

    setPendingKill(null);
    await executeDragMove(itemId, overId, draggedItem, isColumn, overItem, reason);
  };

  const handleDragCancel = () => {
    setActiveItem(null);
  };

  const handleAddItem = (columnId: string, fromNavbar = false) => {
    setDefaultColumn(columnId);
    setShowColumnPicker(fromNavbar);
    setShowCreateModal(true);
  };

  const handleItemCreated = (item: Item) => {
    setItems(prev => [...prev, item]);
  };

  const handleItemClick = (item: Item) => {
    if (isMobile) {
      navigate(`/roadmap/${workspaceId}/item/${item.id}`);
    } else {
      setSelectedItem(item);
    }
  };

  const handleVote = async (itemId: string, direction: 'up' | 'down') => {
    try {
      const result = await itemApi.vote(workspaceId!, itemId, direction);
      setItems(prev => prev.map(item =>
        item.id === itemId
          ? { ...item, vote_count: result.vote_count, user_vote: result.user_vote }
          : item
      ));
      if (selectedItem?.id === itemId) {
        setSelectedItem(prev => prev ? { ...prev, vote_count: result.vote_count, user_vote: result.user_vote } : prev);
      }
    } catch (err) {
      console.error('Failed to vote:', err);
    }
  };

  const handleItemUpdate = (updatedItem: Item) => {
    if (!updatedItem?.id) return;
    setItems(prev => prev.map(item => (item.id === updatedItem.id ? updatedItem : item)));
    setSelectedItem(updatedItem);
  };

  const handleSelectItem = (itemId: string, selected: boolean) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (selected) next.add(itemId);
      else next.delete(itemId);
      return next;
    });
  };

  const handleBulkMove = async (targetColumn: string) => {
    if (selectedItems.size === 0) return;
    setBulkMoving(true);
    try {
      await itemApi.bulkMove(workspaceId!, Array.from(selectedItems), targetColumn);
      setItems(prev => prev.map(item =>
        selectedItems.has(item.id) ? { ...item, board_column: targetColumn } : item
      ));
      setSelectedItems(new Set());
    } catch (err) {
      console.error('Bulk move failed:', err);
    } finally {
      setBulkMoving(false);
    }
  };

  const getCategoryById = (categoryId?: string) =>
    categories.find(c => c.id === categoryId);

  return (
    <div className="flex min-h-screen flex-col bg-gray-50 dark:bg-gray-900">
      <AppNav
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search items..."
      />

      {/* Page header — single row with title + roadmap-specific controls */}
      <div className="max-w-8xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-2">
        <div className="flex items-center justify-between py-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              {currentWorkspace?.name || 'Roadmap'}
            </h1>
            <div className="flex items-center border border-gray-200 dark:border-gray-600 rounded-md overflow-hidden ml-2">
              <button
                onClick={() => setViewMode('board')}
                className={`p-1.5 transition-colors ${viewMode === 'board' ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                title="Board view"
              >
                <LayoutGrid className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 transition-colors ${viewMode === 'list' ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                title="List view"
              >
                <List className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setViewMode('timeline')}
                className={`p-1.5 transition-colors ${viewMode === 'timeline' ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                title="Timeline view"
              >
                <GanttChart className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowFilters(!showFilters)} title="Filters">
              <Filter className="h-4 w-4" />
              {(filterCategory || filterPriority || filterTag || filterAssignee) && (
                <span className="ml-1 h-4 w-4 rounded-full bg-brand-500 text-[10px] text-white flex items-center justify-center">
                  {(filterCategory ? 1 : 0) + (filterPriority ? 1 : 0) + (filterTag ? 1 : 0) + (filterAssignee ? 1 : 0)}
                </span>
              )}
            </Button>
            {(searchQuery || filterCategory || filterPriority || filterTag || filterAssignee) && (
              <Button variant="ghost" size="sm" onClick={() => { setSearchQuery(''); setFilterCategory(''); setFilterPriority(''); setFilterTag(''); setFilterAssignee(''); }}>
                Clear all
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => setShowActivityModal(true)} title="Activity">
              <Activity className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowSettingsModal(true)} title="Settings">
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Filter modal */}
      <FilterModal
        open={showFilters}
        onClose={() => setShowFilters(false)}
        categories={categories}
        tags={tags}
        members={members}
        filterCategory={filterCategory}
        setFilterCategory={setFilterCategory}
        filterPriority={filterPriority}
        setFilterPriority={setFilterPriority}
        filterTag={filterTag}
        setFilterTag={setFilterTag}
        filterAssignee={filterAssignee}
        setFilterAssignee={setFilterAssignee}
        onClearAll={() => { setSearchQuery(''); setFilterCategory(''); setFilterPriority(''); setFilterTag(''); setFilterAssignee(''); }}
      />

      {/* Main content */}
      <main className="flex-1 max-w-8xl mx-auto w-full px-4 py-4 sm:px-6 lg:px-8">
        {loading ? (
          <BoardSkeleton />
        ) : viewMode === 'list' ? (
          <BoardListView
            items={(() => {
              let filtered = items;
              if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                filtered = filtered.filter(item =>
                  item.title.toLowerCase().includes(q) ||
                  (item.description && item.description.toLowerCase().includes(q))
                );
              }
              if (filterCategory) {
                filtered = filtered.filter(item => item.category_id === filterCategory);
              }
              if (filterPriority) {
                filtered = filtered.filter(item => item.priority === filterPriority);
              }
              if (filterTag) {
                filtered = filtered.filter(item => item.tags?.some(t => t.id === filterTag));
              }
              if (filterAssignee) {
                filtered = filtered.filter(item => item.assignees?.some(a => a.id === filterAssignee));
              }
              return filtered;
            })()}
            columns={columns}
            categories={categories}
            onItemClick={setSelectedItem}
            selectedItems={selectedItems}
            onSelectItem={handleSelectItem}
            sortField={listSortField}
            sortDir={listSortDir}
            onSort={(field) => {
              if (field === listSortField) {
                setListSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
              } else {
                setListSortField(field);
                setListSortDir('asc');
              }
            }}
          />
        ) : viewMode === 'timeline' ? (
          /* Timeline View */
          (() => {
            const filteredItems = (() => {
              let filtered = items;
              if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                filtered = filtered.filter(item =>
                  item.title.toLowerCase().includes(q) ||
                  (item.description && item.description.toLowerCase().includes(q))
                );
              }
              if (filterCategory) filtered = filtered.filter(item => item.category_id === filterCategory);
              if (filterPriority) filtered = filtered.filter(item => item.priority === filterPriority);
              if (filterTag) filtered = filtered.filter(item => item.tags?.some(t => t.id === filterTag));
              if (filterAssignee) filtered = filtered.filter(item => item.assignees?.some(a => a.id === filterAssignee));
              return filtered;
            })();

            const now = new Date();
            const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
            const months: Date[] = [];
            for (let i = -1; i < 5; i++) {
              months.push(new Date(monthStart.getFullYear(), monthStart.getMonth() + i, 1));
            }
            const timelineStart = months[0].getTime();
            const timelineEnd = new Date(months[months.length - 1].getFullYear(), months[months.length - 1].getMonth() + 1, 0).getTime();
            const totalMs = timelineEnd - timelineStart;

            const getColumnColor = (slug: string) => {
              const col = columns.find(c => c.id === slug);
              if (col?.color?.startsWith('#')) return col.color;
              const map: Record<string, string> = { now: '#F59E0B', next: '#3B82F6', later: '#8B5CF6', shipped: '#10B981', killed: '#6B7280' };
              return map[slug] || '#6B7280';
            };

            const getItemPosition = (item: Item) => {
              const created = new Date(item.created_at).getTime();
              const due = item.due_date ? new Date(item.due_date).getTime() : created + 30 * 86400000;
              const left = Math.max(0, Math.min(100, ((Math.max(created, timelineStart) - timelineStart) / totalMs) * 100));
              const right = Math.max(0, Math.min(100, ((Math.min(due, timelineEnd) - timelineStart) / totalMs) * 100));
              const width = Math.max(2, right - left);
              return { left, width };
            };

            const todayPct = Math.max(0, Math.min(100, ((now.getTime() - timelineStart) / totalMs) * 100));

            return (
              <div className="overflow-x-auto">
                <div className="min-w-[800px]">
                  {/* Month headers */}
                  <div className="flex border-b border-gray-200 dark:border-gray-700 mb-2">
                    {months.map((m, i) => {
                      const mStart = m.getTime();
                      const mEnd = new Date(m.getFullYear(), m.getMonth() + 1, 0).getTime();
                      const left = ((mStart - timelineStart) / totalMs) * 100;
                      const width = ((mEnd - mStart) / totalMs) * 100;
                      return (
                        <div key={i} className="text-xs font-medium text-gray-500 dark:text-gray-400 py-2 border-r border-gray-100 dark:border-gray-800" style={{ width: `${width}%`, marginLeft: i === 0 ? `${left}%` : undefined }}>
                          {m.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                        </div>
                      );
                    })}
                  </div>
                  {/* Today marker */}
                  <div className="relative">
                    <div className="absolute top-0 bottom-0 w-px bg-red-400 z-10" style={{ left: `${todayPct}%` }}>
                      <div className="absolute -top-5 -translate-x-1/2 text-[10px] font-medium text-red-500 whitespace-nowrap">Today</div>
                    </div>
                    {/* Rows per column */}
                    {columns.map(col => {
                      const colItems = filteredItems.filter(item => item.board_column === col.id);
                      if (colItems.length === 0) return null;
                      return (
                        <div key={col.id} className="mb-4">
                          <div className="flex items-center gap-2 mb-1.5">
                            <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: getColumnColor(col.id) }} />
                            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">{col.name}</span>
                            <span className="text-xs text-gray-400">({colItems.length})</span>
                          </div>
                          <div className="space-y-1 relative">
                            {colItems.map(item => {
                              const pos = getItemPosition(item);
                              return (
                                <div key={item.id} className="relative h-7">
                                  <button
                                    onClick={() => handleItemClick(item)}
                                    className="absolute h-6 rounded-md text-xs font-medium text-white px-2 truncate hover:opacity-90 transition-opacity shadow-sm"
                                    style={{ left: `${pos.left}%`, width: `${pos.width}%`, backgroundColor: getColumnColor(col.id) }}
                                    title={`${item.title}${item.due_date ? ` — Due: ${item.due_date}` : ''}`}
                                  >
                                    {item.title}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })()
        ) : (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            {/* Desktop: 5-column grid — visible at md+ */}
            <div className={`hidden md:grid md:gap-3 lg:gap-4`} style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}>
              {columns.map(column => (
                <BoardColumn
                  key={column.id}
                  column={column}
                  items={getItemsByColumn(column.id)}
                  categories={categories}
                  onAddItem={handleAddItem}
                  onItemClick={handleItemClick}
                  selectedItems={selectedItems}
                  onSelectItem={handleSelectItem}
                  onVote={handleVote}
                />
              ))}
            </div>

            {/* Mobile: Tab-based column switching — visible below md */}
            <div className="md:hidden">
              <Tabs value={mobileTab} onValueChange={setMobileTab}>
                <TabsList className="mb-4 flex w-full overflow-x-auto">
                  {columns.map(column => (
                    <TabsTrigger key={column.id} value={column.id} className="flex-1 min-w-0">
                      <span className="truncate">{column.name}</span>
                      <Badge size="sm" variant="gray" className="ml-1.5">
                        {getItemsByColumn(column.id).length}
                      </Badge>
                    </TabsTrigger>
                  ))}
                </TabsList>

                {columns.map(column => (
                  <TabsContent key={column.id} value={column.id}>
                    <BoardColumn
                      column={column}
                      items={getItemsByColumn(column.id)}
                      categories={categories}
                      onAddItem={handleAddItem}
                      onItemClick={handleItemClick}
                      selectedItems={selectedItems}
                      onSelectItem={handleSelectItem}
                      onVote={handleVote}
                    />
                  </TabsContent>
                ))}
              </Tabs>
            </div>

            {/* Drag overlay — shows a ghost card while dragging */}
            <DragOverlay>
              {activeItem ? (
                <div className="w-64 rotate-3 opacity-90">
                  <BoardItemCard
                    item={activeItem}
                    category={getCategoryById(activeItem.category_id)}
                    onClick={() => {}}
                    isDragDisabled
                  />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </main>

      {/* Create item modal */}
      <CreateItemModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        workspaceId={workspaceId!}
        columns={columns}
        categories={categories}
        tags={tags}
        defaultColumn={defaultColumn}
        showColumnPicker={showColumnPicker}
        onItemCreated={handleItemCreated}
      />

      {/* Command palette */}
      <CommandPalette
        workspaceId={workspaceId}
        onCreateItem={() => handleAddItem('later', true)}
      />

      {/* Bulk actions toolbar */}
      {selectedItems.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-md border border-gray-200 bg-white px-4 py-3 shadow-xl dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center gap-2">
            <CheckSquare className="h-4 w-4 text-brand-500" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {selectedItems.size} selected
            </span>
          </div>
          <div className="h-5 w-px bg-gray-200 dark:bg-gray-700" />
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 dark:text-gray-400">Move to:</span>
            {columns.map(col => (
              <Button
                key={col.id}
                variant="outline"
                size="sm"
                disabled={bulkMoving}
                onClick={() => handleBulkMove(col.id)}
              >
                {col.name}
              </Button>
            ))}
          </div>
          <div className="h-5 w-px bg-gray-200 dark:bg-gray-700" />
          <Button variant="ghost" size="sm" onClick={() => setSelectedItems(new Set())}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Item detail modal */}
      {selectedItem && (
        <ItemDetailModal
          workspaceId={workspaceId!}
          item={selectedItem}
          categories={categories}
          tags={tags}
          onClose={() => setSelectedItem(null)}
          onUpdate={handleItemUpdate}
          onDelete={(itemId) => {
            setItems(prev => prev.filter(i => i.id !== itemId));
            setSelectedItem(null);
          }}
        />
      )}

      {/* Activity modal */}
      <ActivityModal
        open={showActivityModal}
        onClose={() => setShowActivityModal(false)}
        workspaceId={workspaceId!}
      />

      {/* Settings modal */}
      <SettingsModal
        open={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        workspaceId={workspaceId!}
      />

      {/* Kill reason modal */}
      <KillReasonModal
        open={!!pendingKill}
        itemTitle={pendingKill?.title || ''}
        onConfirm={handleKillConfirm}
        onCancel={() => setPendingKill(null)}
      />
    </div>
  );
}
